import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Mail, Globe2, ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SocialLinksRow from "./SocialLinksRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small reusable column header — tight uppercase lime label.
const SectionLabel = ({ children }) => (
  <h4 className="font-['Outfit'] text-xs uppercase tracking-[0.18em] text-[var(--lime)] font-semibold mb-5">
    {children}
  </h4>
);

// Single footer link — explicit flex gap on parent, no space-y to avoid CSS bleed.
const FooterLink = ({ to, children, testId, external = false }) => {
  const cls =
    "group inline-flex items-center gap-1.5 text-sm text-white/65 hover:text-white transition-colors duration-200";
  const inner = (
    <>
      <span>{children}</span>
      <ArrowRight
        size={12}
        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 text-[var(--lime)]"
      />
    </>
  );
  if (external) {
    return (
      <a href={to} className={cls} data-testid={testId}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} data-testid={testId}>
      {inner}
    </Link>
  );
};

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        email: trimmed,
        source: "footer",
      });
      toast.success(data.message);
      setDone(true);
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="mt-2 flex items-start gap-3 rounded-xl border border-[var(--lime)]/30 bg-[var(--lime)]/[0.06] p-4"
        data-testid="footer-newsletter-success"
      >
        <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--lime)] text-[var(--dark-bg)]">
          <Check size={14} strokeWidth={3} />
        </span>
        <div>
          <p className="text-sm font-semibold text-white">You&apos;re on the list.</p>
          <p className="text-xs text-white/60 mt-1 leading-relaxed">
            We&apos;ll email you when a new study opens that might be a fit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-2" data-testid="footer-newsletter-form">
      <div className="flex flex-col sm:flex-row gap-2">
        <label htmlFor="footer-newsletter-email" className="sr-only">
          Email address
        </label>
        <input
          id="footer-newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/10 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[var(--lime)] focus:ring-2 focus:ring-[var(--lime)]/30 text-sm transition-all placeholder:text-white/35"
          data-testid="footer-newsletter-input"
        />
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-1.5 bg-[var(--lime)] text-[var(--dark-bg)] font-semibold px-5 py-3 rounded-lg hover:bg-[var(--lime-dark)] transition-colors text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="footer-newsletter-submit"
        >
          {submitting ? "Sending..." : "Notify me"}
          {!submitting && <ArrowRight size={14} />}
        </button>
      </div>
      <p className="text-base text-white/70 mt-3 leading-relaxed">
        No spam. Just new studies. Unsubscribe anytime.
      </p>
    </form>
  );
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="relative bg-[var(--dark-bg)] border-t border-white/10 grain-dark overflow-hidden"
      data-testid="site-footer"
    >
      {/* Subtle lime glow accent — top-left, behind content */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-32 h-80 w-80 rounded-full bg-[var(--lime)]/[0.06] blur-3xl"
      />

      <div className="relative container-main pt-20 pb-10 md:pt-24 md:pb-12">
        {/* Asymmetric 12-col grid: brand+newsletter on left (5), nav clusters on right (7) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          {/* LEFT: brand + mission + newsletter */}
          <div className="lg:col-span-5 flex flex-col">
            <Link
              to="/"
              className="inline-flex flex-col items-start leading-none mb-3 group"
              data-testid="footer-brand"
            >
              <span className="inline-flex items-baseline gap-2">
                <span className="brand-wordmark text-white text-5xl md:text-6xl">
                  MediTalk
                </span>
                <span className="h-2 w-2 rounded-full bg-[var(--lime)] group-hover:scale-125 transition-transform" />
              </span>
              <span className="italic font-medium text-[15px] md:text-base text-white/80 mt-2 tracking-[0.14em]">
                A brand of Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span> Limited
              </span>
            </Link>
            <p className="text-xs uppercase tracking-[0.18em] text-white/40 font-semibold mb-6">
              Together for better healthcare
            </p>
            <p className="text-base text-white/70 leading-relaxed max-w-md mb-8">
              Giving patients and caregivers the opportunity to get their voices
              heard and shape the future of healthcare.
            </p>

            <div className="max-w-md">
              <SectionLabel>Get notified of new studies</SectionLabel>
              <NewsletterForm />
            </div>
          </div>

          {/* RIGHT: four columns of links */}
          <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-2 sm:grid-cols-4 gap-10 sm:gap-8">
            {/* Platform */}
            <div>
              <SectionLabel>Platform</SectionLabel>
              <nav className="flex flex-col gap-3" aria-label="Platform">
                <FooterLink to="/about" testId="footer-link-about">
                  About
                </FooterLink>
                <FooterLink to="/#how-it-works" testId="footer-link-how-it-works">
                  How it works
                </FooterLink>
                <FooterLink to="/reviews" testId="footer-link-reviews">
                  Reviews
                </FooterLink>
                <FooterLink to="/research" testId="footer-link-research-index">
                  Browse by condition
                </FooterLink>
                <FooterLink to="/faq" testId="footer-link-faq">
                  FAQ
                </FooterLink>
              </nav>
            </div>

            {/* Participate */}
            <div>
              <SectionLabel>Participate</SectionLabel>
              <nav className="flex flex-col gap-3" aria-label="Participate">
                <FooterLink to="/patient" testId="footer-link-patient">
                  I&apos;m a patient
                </FooterLink>
                <FooterLink to="/caregiver" testId="footer-link-caregiver">
                  I&apos;m a caregiver
                </FooterLink>
              </nav>
            </div>

            {/* Join the community */}
            <div>
              <SectionLabel>Join the community</SectionLabel>
              <nav className="flex flex-col gap-3" aria-label="Join the community">
                <FooterLink to="/patient/sign-up" testId="footer-link-patient-signup">
                  Join as a patient
                </FooterLink>
                <FooterLink to="/caregiver/sign-up" testId="footer-link-caregiver-signup">
                  Join as a caregiver
                </FooterLink>
                <FooterLink to="/account/request-link" testId="footer-link-update">
                  Update your info
                </FooterLink>
              </nav>
            </div>

            {/* Contact */}
            <div>
              <SectionLabel>Contact</SectionLabel>
              <div className="flex flex-col gap-4">
                <a
                  href="mailto:hello@meditalk.world"
                  className="group inline-flex items-start gap-3 text-sm text-white/70 hover:text-white transition-colors"
                  data-testid="footer-link-email"
                >
                  <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04] border border-white/10 group-hover:border-[var(--lime)]/40 group-hover:bg-[var(--lime)]/10 transition-colors flex-shrink-0">
                    <Mail size={14} className="text-[var(--lime)]" />
                  </span>
                  <span className="pt-1.5">hello@meditalk.world</span>
                </a>
                <div className="inline-flex items-start gap-3 text-sm text-white/70">
                  <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04] border border-white/10 flex-shrink-0">
                    <Globe2 size={14} className="text-[var(--lime)]" />
                  </span>
                  <span className="pt-1.5">Operating globally</span>
                </div>

                {/* Social row , small, monochrome, with UTM tags so
                    we can see in PostHog which placement actually
                    drives follower clicks. */}
                <div className="pt-3 mt-1 border-t border-white/10">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 font-semibold mb-3">
                    Follow for study announcements
                  </p>
                  <SocialLinksRow
                    source="site"
                    medium="footer"
                    campaign="follow"
                    variant="dark"
                    size="md"
                    testIdPrefix="footer-social"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal disclosure (Companies Act 2006 + UK GDPR identity) */}
        <div className="mt-16 md:mt-20 pt-8 border-t border-white/10">
          <p className="text-[12.5px] text-white/55 leading-relaxed max-w-4xl">
            <strong className="text-white/75">MediTalk</strong> is a trading
            brand of <strong className="text-white/75">Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span> Limited</strong>,
            a company registered in England &amp; Wales (company number{" "}
            <span className="text-white/75">04504403</span>). Registered office:
            2nd Floor Medway Bridge House, 1–8 Fairmeadow, Maidstone, Kent,
            United Kingdom, ME14 1JP. VAT{" "}
            <span className="text-white/75">GB 799 9924 23</span>. ICO
            registration <span className="text-white/75">Z1909295</span>.
          </p>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 text-[12px] text-white/45">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <p data-testid="footer-copyright">
                © {currentYear} Medicys Limited. All rights reserved.
              </p>
              <Link
                to="/privacy"
                className="hover:text-white transition-colors"
                data-testid="footer-link-privacy"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="hover:text-white transition-colors"
                data-testid="footer-link-terms"
              >
                Terms
              </Link>
              <Link
                to="/cookies"
                className="hover:text-white transition-colors"
                data-testid="footer-link-cookies"
              >
                Cookies
              </Link>
            </div>
            <p className="inline-flex items-center gap-1.5">
              Made with
              <Heart
                size={12}
                className="text-[var(--lime)] fill-[var(--lime)]"
                aria-label="love"
              />
              for the patient community
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
